import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import { ROSTER_SLOTS } from '../types/protocol'
import type { DraftResponse, DraftState, RosterSlot } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
  onDraft: (draft: DraftState) => void
}

const MAX_SLOT_COUNT = 30

export default function RosterConfigEditor({ draft, commissionerToken, onDraft }: Props) {
  const [pendingSlot, setPendingSlot] = useState<RosterSlot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const locked = draft.status !== 'setup'

  async function adjust(slot: RosterSlot, delta: number) {
    const count = draft.roster_config[slot] + delta
    if (count < 0 || count > MAX_SLOT_COUNT) return

    setPendingSlot(slot)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/settings`, {
        method: 'PATCH',
        commissionerToken,
        body: { roster_config: { ...draft.roster_config, [slot]: count } },
      })
      onDraft(response.draft)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update the roster')
    } finally {
      setPendingSlot(null)
    }
  }

  return (
    <section className="panel p-6">
      <div className="flex items-baseline justify-between">
        <h2 className="eyebrow">Roster</h2>
        <p className="text-sm text-ink-mid">
          Rounds: <span className="font-semibold text-signal">{draft.rounds}</span>
        </p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {ROSTER_SLOTS.map((slot) => (
          <div key={slot} className="rounded-lg border border-line bg-ink p-3 text-center">
            <p className="text-xs font-semibold tracking-wide text-ink-mid">{slot}</p>
            <div className="mt-2 flex items-center justify-center gap-3">
              <button
                type="button"
                aria-label={`Fewer ${slot}`}
                onClick={() => void adjust(slot, -1)}
                disabled={locked || pendingSlot !== null || draft.roster_config[slot] === 0}
                className="h-8 w-8 rounded-full bg-surface-3 text-lg leading-none hover:bg-line-strong disabled:opacity-40"
              >
                −
              </button>
              <span className="w-6 text-lg font-semibold tabular-nums">{draft.roster_config[slot]}</span>
              <button
                type="button"
                aria-label={`More ${slot}`}
                onClick={() => void adjust(slot, 1)}
                disabled={locked || pendingSlot !== null || draft.roster_config[slot] === MAX_SLOT_COUNT}
                className="h-8 w-8 rounded-full bg-surface-3 text-lg leading-none hover:bg-line-strong disabled:opacity-40"
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="mt-3 text-sm text-crit">{error}</p>}
    </section>
  )
}
